import {
  Paper,
  styled,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
} from "@mui/material";
import { IOpsLinkTopic, IFlight, IOpsLinkMessage } from "@vmosolutions/oc-interfaces";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { createEvent } from "src/redux/reducers/OpsLinkReducer";
import { OpsLinkMessagePriority } from "@vmosolutions/oc-interfaces";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { OpsLinkFlightRow } from "./OpsLinkFlightRow";
import moment from "moment-timezone";
import * as Yup from "yup";
import { isDevEnv } from "src/utils";
import { isHumanWriteableTopic } from "./shared/humanWriteableTopic";

interface IOpsLinkCreateEvent {
  id: string;
  replyEvent?: IOpsLinkMessage;
  trackedFlights?: IFlight[];
}

const OCOpsLinkCreateEvent = styled(Paper)(({ theme }) => ({
  padding: "5px",
  width: "100%",
  flex: "1 1 auto",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  fieldset: {
    border: "1px solid rgba(255, 255, 255, 0.23) !important",
  },
  "& .Mui-error": {
    fieldset: {
      border: `1px solid ${theme.palette.error.light} !important`,
    },
  },
  "& .entryField": {
    height: "2em",
    maxHeight: "2em",
  },
}));

const getLocalStart = (flight: IFlight) => {
  return flight.actualOut && flight.originAirport?.timezoneId
    ? moment.utc(flight.actualOut).tz(flight.originAirport?.timezoneId)?.format("MMM Do, Y HH:mm z")
    : null;
};

const getLocalEnd = (flight: IFlight) => {
  return flight.actualIn && flight.destAirport?.timezoneId
    ? moment.utc(flight.actualIn).tz(flight.destAirport?.timezoneId)?.format("MMM Do, Y HH:mm z")
    : null;
};

const eventSchema = Yup.object().shape({
  flight: Yup.string().required("Flight is a required field").nullable(),
  topic: Yup.string().required("Topic is a required field").nullable(),
  eventType: Yup.string().nullable(),
  priority: Yup.string().required("Priority is a required field").nullable(),
  text: Yup.string().required("Text is a required field"),
});

export default function OpsLinkCreateEvent(props: IOpsLinkCreateEvent): JSX.Element {
  const { id, replyEvent, trackedFlights } = props;
  const dispatch = useDispatch();
  const topics = useSelector((state: IRootState) => state.OpsLinkReducer.topicList);
  const replyTopic = replyEvent?.topic;

  const initVals = {
    flight: replyEvent?.flight ?? "",
    // Only carry the topic over when users may post on it, so a reply can never be sent on a machine-only topic
    topic: isHumanWriteableTopic(replyTopic) ? replyTopic._id : "",
    eventType: replyEvent?.eventType ?? "",
    priority: replyEvent?.priority ?? OpsLinkMessagePriority.MEDIUM,
    text: "",
    fromCrew: false,
  };

  const formik = useFormik({
    initialValues: initVals,
    validationSchema: eventSchema,
    onSubmit: async (values: any) => {
      const newEvent: IOpsLinkMessage = {
        flight: values.flight,
        topic: values.topic,
        eventTimestamp: new Date(),
        source: values.fromCrew ? "CrewApp-Internet" : "OC",
        fromCrew: values.fromCrew,
        priority: values.priority as OpsLinkMessagePriority,
        acknowledgedBy: [],
        text: values.text,
        eventType: values.eventType,
        createdAt: new Date(),
        contentHash: "",
      };

      await dispatch(createEvent({ flightId: formik.values.flight as string, body: newEvent }));

      formik.resetForm({
        values: initVals,
      });
    },
  });

  return (
    <OCOpsLinkCreateEvent elevation={1}>
      <form
        onSubmit={formik.handleSubmit}
        style={{ height: "100%", width: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        <Grid container spacing={1} sx={{ width: "100%", margin: 0, flexShrink: 0 }}>
          <Grid item xs={6}>
            <FormControl fullWidth error={!!formik?.errors?.flight}>
              <InputLabel shrink={true} id="flightSelectLabel">
                Flight
              </InputLabel>
              <Select
                id="flightSelect"
                notched={true}
                name="flight"
                label="Flight"
                className="entryField"
                labelId="flightSelectLabel"
                onChange={formik.handleChange}
                value={formik.values.flight}
                renderValue={(selected) => (
                  <OpsLinkFlightRow id={id} flight={trackedFlights.find((tf) => tf._id === selected.valueOf())} />
                )}
              >
                {trackedFlights?.length > 0 &&
                  trackedFlights.map((trackedFlight: IFlight, index: number) => {
                    return (
                      <MenuItem key={trackedFlight._id} id={`flights${index}`} value={trackedFlight._id}>
                        <OpsLinkFlightRow id={id} flight={trackedFlight} />
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth error={!!formik?.errors?.topic}>
              <InputLabel shrink={true} id="topicsSelectLabel">
                Topic
              </InputLabel>
              <Select
                id="topicsSelect"
                notched={true}
                name="topic"
                className="entryField"
                label="Topic"
                labelId="topicsSelectLabel"
                value={formik.values.topic}
                onChange={formik.handleChange}
              >
                {topics?.length > 0 &&
                  topics.filter(isHumanWriteableTopic).map((topic: IOpsLinkTopic, index: number) => {
                    return (
                      <MenuItem key={topic._id} id={`topic${index}`} value={topic._id}>
                        {topic.longName}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              id="eventType"
              name="eventType"
              value={formik.values.eventType}
              onChange={formik.handleChange}
              label="Subject"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                className: "entryField",
                sx: { padding: "0 0 0 10px" },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth error={!!formik?.errors?.priority}>
              <InputLabel shrink={true} id="prioritySelectLabel">
                Priority
              </InputLabel>
              <Select
                id="prioritySelect"
                name="priority"
                labelId="prioritySelectLabel"
                value={formik.values.priority}
                onChange={formik.handleChange}
                className="entryField"
                label="Priority"
                notched={true}
              >
                {Object.entries(OpsLinkMessagePriority).map(([key, value], index) => (
                  <MenuItem key={key} id={`priority${index}`} value={value}>
                    {value.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ flexGrow: 1, minHeight: "56px", display: "flex", flexDirection: "column", mt: 1, px: "4px" }}>
          <TextField
            fullWidth
            multiline
            error={!!formik?.errors?.text}
            variant="outlined"
            value={formik.values.text}
            id="text"
            name="text"
            label="Text"
            onChange={formik.handleChange}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              "& .MuiInputBase-root": {
                flex: 1,
                minHeight: 0,
                alignItems: "stretch",
              },
              "& .MuiInputBase-inputMultiline": {
                height: "100% !important",
                overflow: "auto !important",
                boxSizing: "border-box",
              },
            }}
          />
        </Box>
        <Box sx={{ mt: 1, px: "4px" }}>
          <Button size="small" type="submit" variant="contained" sx={{ marginLeft: "5px" }}>
            Submit
          </Button>
          {isDevEnv() && (
            <FormControlLabel
              sx={{ ml: 1, color: "dev.main" }}
              control={
                <Checkbox
                  size="small"
                  name="fromCrew"
                  checked={formik.values.fromCrew}
                  onChange={formik.handleChange}
                  sx={{ color: "dev.main", "&.Mui-checked": { color: "dev.main" } }}
                />
              }
              label="fromCrew (dev)"
            />
          )}
        </Box>
      </form>
    </OCOpsLinkCreateEvent>
  );
}
